import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { supabase } from '@/lib/supabase';
import {
  FxAiCategorizationResult,
  FxAiLearnFromImportResult,
  FxAiLearnFromFeedbackResult,
} from '@/types/api/finance';

async function invokeFinanceAiFunction(body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/finance-ai`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `AI function error ${res.status}`);
  }

  return res.json();
}

const financeAiService = {
  async categorizeTransactions(params: {
    transactions: Array<{
      tempId: string;
      date: string;
      description: string;
      amount: number;
      type?: string;
    }>;
    vendors: Array<{
      id: string;
      name: string;
      displayName: string | null;
    }>;
    categories: Array<{
      id: string;
      name: string;
      type?: string;
    }>;
    sourceAccount?: string;
  }): Promise<{ results: FxAiCategorizationResult[] }> {
    return invokeFinanceAiFunction({
      action: 'categorizeTransactions',
      ...params,
    });
  },

  async learnFromImport(params: {
    corrections: Array<{
      description: string;
      assignedVendorName: string;
      assignedCategoryName: string;
      originalSuggestion: string | null;
    }>;
    acceptedRows: Array<{
      description: string;
      vendorName: string;
      categoryName: string;
      matchSource: string | null;
    }>;
    existingRules: Array<{
      matchPattern: string;
      matchType: string;
      vendorName: string;
    }>;
  }): Promise<FxAiLearnFromImportResult> {
    return invokeFinanceAiFunction({
      action: 'learnFromImport',
      ...params,
    });
  },

  async learnFromFeedback(params: {
    recentOverrides: Array<{
      description: string;
      amount: number;
      prevVendor: string;
      prevCategory: string;
      newVendor: string;
      newCategory: string;
    }>;
    vendorAffinities: Array<{
      vendorName: string;
      categories: Array<{ name: string; percentage: number }>;
    }>;
    existingRules: Array<{
      matchPattern: string;
      matchType: string;
      vendorName: string;
      hitCount: number;
      overrideCount: number;
    }>;
    staleRules: Array<{
      id: string;
      matchPattern: string;
      accuracy: number;
    }>;
  }): Promise<FxAiLearnFromFeedbackResult> {
    return invokeFinanceAiFunction({
      action: 'learnFromFeedback',
      ...params,
    });
  },

  async suggestVendor(params: {
    description: string;
    vendors: Array<{ name: string; displayName: string | null }>;
    categories: Array<{ id: string; name: string }>;
  }): Promise<{
    vendorName: string;
    isNewVendor: boolean;
    categoryName: string;
    confidence: number;
    reasoning: string;
  }> {
    return invokeFinanceAiFunction({
      action: 'suggestVendor',
      ...params,
    });
  },
};

export default financeAiService;
